import React, { useState, useEffect } from 'react';
import { X, Users, Mail, CheckCircle, XCircle, Clock, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';
import { getCampaignStats, getCampaignEnrollments, DripCampaign, DripCampaignEnrollment } from '@/actions/drip-campaigns';

interface CampaignStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: DripCampaign;
}

interface CampaignStats {
  campaign: {
    total_enrolled: number;
    total_completed: number;
    total_unsubscribed: number;
  };
  enrollments: Array<{
    status: string;
    count: number;
  }>;
  executions: {
    pending: number;
    sent: number;
    failed: number;
  };
}

const CampaignStatsModal: React.FC<CampaignStatsModalProps> = ({
  isOpen,
  onClose,
  campaign,
}) => {
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [enrollments, setEnrollments] = useState<DripCampaignEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');

  useEffect(() => {
    if (isOpen && campaign) {
      loadStats();
      loadEnrollments();
    }
  }, [isOpen, campaign, filterStatus]);

  const loadStats = async () => {
    try {
      const response = await getCampaignStats(campaign.id);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load campaign statistics');
    }
  };

  const loadEnrollments = async () => {
    try {
      setLoading(true);
      const response = await getCampaignEnrollments(campaign.id, {
        status: filterStatus || undefined,
      });
      if (response.success && response.data) {
        setEnrollments(response.data);
      }
    } catch (error) {
      console.error('Error loading enrollments:', error);
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      unsubscribed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      bounced: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return badges[status as keyof typeof badges] || badges.active;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Campaign Statistics
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {campaign.campaign_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Enrolled */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                    <Users className="w-6 h-6 text-brandColor dark:text-green-400" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Enrolled
                  </h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.campaign.total_enrolled}
                </p>
              </div>

              {/* Total Completed */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Completed
                  </h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.campaign.total_completed}
                </p>
                {stats.campaign.total_enrolled > 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {((stats.campaign.total_completed / stats.campaign.total_enrolled) * 100).toFixed(1)}% completion rate
                  </p>
                )}
              </div>

              {/* Total Unsubscribed */}
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Unsubscribed
                  </h3>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.campaign.total_unsubscribed}
                </p>
                {stats.campaign.total_enrolled > 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {((stats.campaign.total_unsubscribed / stats.campaign.total_enrolled) * 100).toFixed(1)}% unsubscribe rate
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Enrollment Breakdown */}
          {stats && stats.enrollments.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Enrollment Status Breakdown
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {stats.enrollments.map((item) => (
                  <div
                    key={item.status}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center"
                  >
                    <span
                      className={`inline-block px-3 py-1 text-xs font-medium rounded mb-2 ${getStatusBadge(
                        item.status
                      )}`}
                    >
                      {item.status}
                    </span>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {item.count}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Execution Stats */}
          {stats && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Execution Stats
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.executions.pending}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Sent</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.executions.sent}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Failed</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.executions.failed}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Enrollments List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="w-5 h-5" />
                Enrolled Leads
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterStatus('')}
                  className={`px-3 py-1 text-sm rounded ${
                    filterStatus === ''
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterStatus('active')}
                  className={`px-3 py-1 text-sm rounded ${
                    filterStatus === 'active'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilterStatus('completed')}
                  className={`px-3 py-1 text-sm rounded ${
                    filterStatus === 'completed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : enrollments.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400">No enrollments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Lead
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Current Step
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Enrolled At
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {enrollments.map((enrollment) => (
                      <tr
                        key={enrollment.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {enrollment.lead?.name || 'Unknown Lead'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {enrollment.lead?.email || enrollment.lead_uuid}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-1 text-xs font-medium rounded ${getStatusBadge(
                              enrollment.status
                            )}`}
                          >
                            {enrollment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          Step {enrollment.current_step_order}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(enrollment.enrolled_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {enrollment.emails_sent} sent
                            </div>
                            {enrollment.emails_opened > 0 && (
                              <div className="text-xs text-green-600 dark:text-green-400">
                                {enrollment.emails_opened} opened
                              </div>
                            )}
                            {enrollment.emails_clicked > 0 && (
                              <div className="text-xs text-brandColor dark:text-green-400">
                                {enrollment.emails_clicked} clicked
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignStatsModal;
